// 📁 src/context/AuthContext.jsx
import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth, db } from '../config/firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { useNavigate, useLocation } from 'react-router-dom';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [checkingUser, setCheckingUser] = useState(true);
  const [profileComplete, setProfileComplete] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const checkProfileCompletion = (data) => {
    return !!data.accountType && !!data.name && (!!data.cpf || !!data.cnpj);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        // Etapa 1: Define um usuário básico imediatamente para evitar estado nulo durante a transição.
        setUser({ uid: currentUser.uid, email: currentUser.email, photoURL: currentUser.photoURL });
        setCheckingUser(true); // Indica que ainda estamos buscando o perfil completo.

        try {
          // Etapa 2: Busca os dados completos do Firestore.
          const userId = currentUser.uid;
          const docRef = doc(db, 'users', userId);
        const settingsRef = doc(db, 'users', userId, 'settings', 'main');

          const [docSnap, settingsSnap] = await Promise.all([
            getDoc(docRef),
            getDoc(settingsRef)
          ]);

          const settings = settingsSnap.exists() ? settingsSnap.data() : {};

          if (docSnap.exists()) {
            const userData = docSnap.data();
            const isComplete = checkProfileCompletion(userData);
            // Atualiza o usuário com os dados completos do Firestore.
            setUser(prevUser => ({
              ...prevUser,
              ...userData,
              settings
            }));
            setProfileComplete(isComplete);
          } else {
            // Se o documento não existe, o perfil não está completo.
            setUser(prevUser => ({ ...prevUser, settings }));
            setProfileComplete(false);
          }
        } catch (error) {
          console.error('Erro ao buscar dados do Firestore:', error);
          // Mantém o usuário básico, mas considera o perfil incompleto.
          setProfileComplete(false);
        } finally {
          setCheckingUser(false);
        }
      } else {
        // Nenhum usuário logado.
        setUser(null);
        setProfileComplete(false);
        setCheckingUser(false);
      }
    });
  
    return () => unsubscribe();
  }, []);
  
  return (
    <AuthContext.Provider value={{ user, checkingUser, profileComplete }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
export { AuthContext };
